import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { lazy, Suspense, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../stores/auth-store';
import { useRealtime } from '../hooks/use-realtime';
import { setTrackingUser } from '../lib/tracking/tracking-client';
import { useGeoBlock } from '../hooks/use-geo-block';
import { GeoBlockedPage } from '../features/geo/geo-blocked-page';

const ChatPanel = lazy(() =>
  import('../features/chat/chat-panel').then((module) => ({
    default: module.ChatPanel,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { loadProfile } = useAuthStore();
  const { isBlocked } = useGeoBlock();

  // Initialize realtime subscriptions
  useRealtime();

  useEffect(() => {
    // Initialize rememberMe preference if not set
    if (!localStorage.getItem('candle-remember-me')) {
      localStorage.setItem('candle-remember-me', 'true');
    }

    let cancelled = false;

    // getSession() is the source of truth for first paint. onAuthStateChange
    // can emit INITIAL_SESSION with a null session before storage hydrates;
    // treating that as signed-out wiped chrome on public memorial routes.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      useAuthStore.setState({
        session,
        user: session?.user ?? null,
        isAuthenticated: !!session?.user,
        isLoading: false,
        ...(session?.user ? {} : { profile: null, profileLoaded: true }),
      });

      setTrackingUser(session?.user?.id ?? null, session?.access_token ?? null);

      if (session?.user) {
        loadProfile(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      // INITIAL_SESSION is handled by getSession() above. A null payload here
      // used to flip AppHeader into About / Features / Sign In for users who
      // were already signed in — most visible on public memorial pages.
      if (event === 'INITIAL_SESSION') return;

      if (!session) {
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          useAuthStore.setState({
            session: null,
            user: null,
            isAuthenticated: false,
            profile: null,
            profileLoaded: true,
            isLoading: false,
          });
          setTrackingUser(null, null);
        }
        return;
      }

      useAuthStore.setState({
        session,
        user: session.user,
        isAuthenticated: true,
      });

      setTrackingUser(session.user.id, session.access_token);

      loadProfile(session.user.id).catch(() => {
        /* profile retry lives in the store */
      });

      if (event === 'SIGNED_IN') {
        import('../lib/auth/auth-service').then(({ registerDevice }) => {
          registerDevice(session).catch(console.error);
        });
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  // Fail-open: do not block first paint on the IP lookup. The edge function
  // already fails open; waiting here made the landing rails feel stalled.
  if (isBlocked) return <GeoBlockedPage />;

  return <>{children}</>;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {children}
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px border hsl(var(--border))',
              padding: '12px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            },
            success: {
              iconTheme: {
                primary: 'hsl(var(--primary))',
                secondary: 'hsl(var(--primary-foreground))',
              },
            },
            error: {
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
          }}
        />
        {/* Global Chat Panel */}
        {isAuthenticated && !window.location.pathname.startsWith('/screen') && (
          <Suspense fallback={null}>
            <ChatPanel />
          </Suspense>
        )}
      </AuthProvider>
    </QueryClientProvider>
  );
}
